import { NgModule, FactoryProvider, Optional, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PershingGridHeaderComponent, PershingGrid2Module } from '@pershing/pershing-grid2';
import { TranslateModule } from '@ngx-translate/core';
import { ResponsiveModule } from 'ngx-responsive';
import { AgGridModule } from '@ag-grid-community/angular';
import { PershingCoreModule, PershingTranslationService } from '@pershing/pershing-core';
import { PershingCommonModule } from '@pershing/pershing-common';


import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher, MatOptionModule, MatNativeDateModule } from '@angular/material/core';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialogModule } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';

import { QuoteComponent } from './components/container/quote/quote.component';
import { BalancesComponent } from './components/balances/balances.component';
import { HoldingComponent } from './components/container/holding/holding.component';
import { HoldingContainerComponent } from './components/container/holding/holding-container.component';
import { AccountInfoComponent } from './components/container/account/accountInfo.component';

import { TradingService } from './services/common/trading-service.service';
import { TradingServiceStrategy } from './services/common/trading-service-strategy.service';
import { ProfessionalTradingServiceStrategy } from './services/common/professional-trading-service-strategy.service';
import { RetailTradingServiceStrategy } from './services/common/retail-trading-service-strategy.service';
import { EquityTradingService } from './services/equity/equity-trading-service.service';
import { EquityTradingServiceStrategy } from './services/equity/equity-trading-service-strategy.service';
import { ProfessionalEquityTradingServiceStrategy } from './services/equity/professional-equity-trading-service-strategy.service';
import { RetailEquityTradingServiceStrategy } from './services/equity/retail-equity-trading-service-strategy.service';
import { MutualFundTradingService } from './services/mutualfund/mutualfund-trading-service.service';
import { MutualFundTradingServiceStrategy } from './services/mutualfund/mutualfund-trading-service-strategy.service';
import { ProfessionalMutualFundTradingServiceStrategy } from './services/mutualfund/professional-mutualfund-trading-service-strategy.service';
import { RetailMutualFundTradingServiceStrategy } from './services/mutualfund/retail-mutualfund-trading-service-strategy.service';
import { OptionTradingService } from './services/option/option-trading-service.service';
import { RetailOptionTradingServiceStrategy } from './services/option/retail-option-trading-service-strategy.service';
import { ProfessionalOptionTradingServiceStrategy } from './services/option/professional-option-trading-service-strategy.service';
import { OptionTradingServiceStrategy } from './services/option/option-trading-service-strategy.service';
import { BalancesService } from './services/balances/balances-service.service';
import { ComplianceService } from './services/compliance/compliance-service.service';
import { SymbolSearchAutoCompleteComponent } from './components/common/symbol-search-auto-complete/symbol-search-auto-complete.component';
import { OrderStatusService } from './services/common/order-status-service.service';
import { SymbolService } from './services/common/symbol.service';
import { GridExportService } from './services/common/grid-export-service.service';
import { TermsAndConditionsDialog } from './components/common/terms-and-conditions/terms-and-conditions-dialog.component';
import { QuoteMobileDialog } from './components/mobile/quote-dialog.component';
import { ExtendedHoursDialog } from './components/mobile/extended-hours-dialog.component';
import { BalancesMobileDialog } from './components/mobile/balances-dialog.component';
import { HoldingsMobileDialog } from './components/mobile/holdings-dialog.component';
import { BalancesMiniComponent } from './components/mobile/balances-mini.component';
import { HoldingsMiniComponent } from './components/mobile/holdings-mini.component';
import { QuoteMiniComponent } from './components/mobile/quote-mini.component';
import { TaxLotComponent } from './components/common/tax-lot/tax-lot.component';
import { TaxLotGridComponent } from './components/common/tax-lot/tax-lot-grid.component';
import { TaxLotService } from './services/tax-lot/tax-lot.service';
import { TradingDataService } from './services/common/trading-data-service.service';
import { TaxLotReviewComponent } from './components/common/tax-lot/tax-lot-review.component';

import { PopoverComponent } from './components/common/popover/popover.component';
import { Popover } from './components/common/popover/popover.service';
import { OrderSettingsComponent } from './components/common/order-settings/order-settings.component';
import { CancelOrderComponent } from './components/common/cancel-order/cancel-order.component';
import { DisclaimersComponent } from './components/common/disclaimers/disclaimers.component';
import { AlertMessageDialogComponent } from './components/common/alert-message-dialog/alert-message-dialog.component';
import { CancelEquityOrderDetailsComponent } from './components/common/cancel-order/cancel-equity-order-details/cancel-equity-order-details.component';
import { CancelMfOrderDetailsComponent } from './components/common/cancel-order/cancel-mf-order-details/cancel-mf-order-details.component';
import { CancelOptionsOrderDetailsComponent } from './components/common/cancel-order/cancel-options-order-details/cancel-options-order-details.component';
import { OrderSummaryComponent } from './components/order-summary/order-summary.component';
import { PageContainerComponent } from './components/container/page-container/page-container.component';
import { QtyCalculatorComponent } from './components/common/qty-calculator/qty-calculator.component';
import { TradingAllocationService } from './services/common/trading-allocation-service.service';
import { TradingAccountPickerComponent } from './components/account/account-picker.component';
import { OrderProspectusFormComponent } from './components/common/order-prospectus/order-prospectus-form.component';
import { PhoneMaskDirective } from './directives/phone-mask.directive';
import { EligibleAccountsComponent } from './components/account/eligible-accounts.component';
import { CancelAnnuityOrderDetailsComponent } from './components/common/cancel-order/cancel-annuity-order-details/cancel-annuity-order-details.component';
import { FundMinimumsComponent } from './components/common/fund-minimums/fund-minimums-component';
import { DateMaskDirective } from './directives/date-mask.directive';
import { TimeMaskDirective } from './directives/time-mask.directive';
import { MatOptionHeightDirective } from './directives/app-mat-option-height.directive';
import { TradingResponsiveComponent } from './components/trading-responsive/trading-responsive.component';
const PERSHING_TRADING_COMPONENTS: any[] = [
  TradingResponsiveComponent,
  QuoteComponent,
  QuoteMobileDialog,
  ExtendedHoursDialog,
  QuoteMiniComponent,
  BalancesComponent,
  AccountInfoComponent,
  OrderSummaryComponent,
  BalancesMobileDialog,
  BalancesMiniComponent,
  OrderSettingsComponent,
  OrderProspectusFormComponent,
  CancelOrderComponent,
  PageContainerComponent,
  AlertMessageDialogComponent,
  CancelEquityOrderDetailsComponent,
  CancelMfOrderDetailsComponent,
  CancelOptionsOrderDetailsComponent,
  HoldingComponent,
  HoldingContainerComponent,
  HoldingsMobileDialog,
  HoldingsMiniComponent,
  TaxLotComponent,
  TaxLotGridComponent,
  TaxLotReviewComponent,
  PopoverComponent,
  DisclaimersComponent,
  SymbolSearchAutoCompleteComponent,
  TermsAndConditionsDialog,
  QtyCalculatorComponent,
  TradingAccountPickerComponent,
  PhoneMaskDirective,
  DateMaskDirective,
  TimeMaskDirective,
  MatOptionHeightDirective,
  EligibleAccountsComponent,
  CancelAnnuityOrderDetailsComponent,
  FundMinimumsComponent
];

const PERSHING_TRADING_ENTRY_COMPONENTS: any[] = [
  BalancesComponent,
  AccountInfoComponent,
  QuoteMobileDialog,
  ExtendedHoursDialog,
  BalancesMobileDialog,
  HoldingsMobileDialog,
  TermsAndConditionsDialog,
  PopoverComponent,
  TaxLotComponent,
  OrderSettingsComponent,
  OrderProspectusFormComponent,
  CancelOrderComponent,
  PageContainerComponent,
  HoldingComponent,
  HoldingContainerComponent,
  DisclaimersComponent,
  AlertMessageDialogComponent,
  OrderSummaryComponent,
  QtyCalculatorComponent,
  TradingAccountPickerComponent,
  EligibleAccountsComponent,
  FundMinimumsComponent
];

// TODO This may eventually go away once we have strategy based services for all asset types
const PERSHING_TRADING_SERVICE_PROVIDERS: any[] = [
  TradingService,
  EquityTradingService,
  MutualFundTradingService,
  OptionTradingService,
  BalancesService,
  ComplianceService,
  OrderStatusService,
  SymbolService,
  GridExportService,
  TaxLotService,
  TradingDataService,
  Popover,
  TradingAllocationService
];

const PERSHING_TRADING_SERVICE_STRATEGIES_PROVIDERS: any[] = [
  RetailTradingServiceStrategy,
  ProfessionalTradingServiceStrategy,
  RetailEquityTradingServiceStrategy,
  ProfessionalEquityTradingServiceStrategy,
  RetailMutualFundTradingServiceStrategy,
  ProfessionalMutualFundTradingServiceStrategy,
  RetailOptionTradingServiceStrategy,
  ProfessionalOptionTradingServiceStrategy
];

export function equityTradingServiceFactory(...strategies: Array<EquityTradingServiceStrategy>): EquityTradingService {
  return new EquityTradingService(strategies);
}

export function mutualFundTradingServiceFactory(...strategies: Array<MutualFundTradingServiceStrategy>): MutualFundTradingService {
  return new MutualFundTradingService(strategies);
}

export function optionTradingServiceFactory(...strategies: Array<OptionTradingServiceStrategy>): OptionTradingService {
  return new OptionTradingService(strategies);
}

export function tradingServiceFactory(...strategies: Array<TradingServiceStrategy>): TradingService {
  return new TradingService(strategies);
}


const EQUITY_TRADING_SERVICE_PROVIDERS_FACTORY: FactoryProvider = {
  provide: EquityTradingService,
  useFactory: equityTradingServiceFactory,
  deps: [
      RetailEquityTradingServiceStrategy,
      ProfessionalEquityTradingServiceStrategy
  ]
};

const MUTUAL_FUND_TRADING_SERVICE_PROVIDERS_FACTORY: FactoryProvider = {
  provide: MutualFundTradingService,
  useFactory: mutualFundTradingServiceFactory,
  deps: [
      RetailMutualFundTradingServiceStrategy,
      ProfessionalMutualFundTradingServiceStrategy
  ]
};

const OPTION_TRADING_SERVICE_PROVIDERS_FACTORY: FactoryProvider = {
  provide: OptionTradingService,
  useFactory: optionTradingServiceFactory,
  deps: [
      RetailOptionTradingServiceStrategy,
      ProfessionalOptionTradingServiceStrategy
  ]
};

const TRADING_SERVICE_PROVIDERS_FACTORY: FactoryProvider = {
  provide: TradingService,
  useFactory: tradingServiceFactory,
  deps: [
      RetailTradingServiceStrategy,
      ProfessionalTradingServiceStrategy
  ]
};


@NgModule({
  imports: [
    CommonModule,
    HttpClientModule,
    FormsModule,
    MatAutocompleteModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatRadioModule,
    MatSelectModule,
    MatTabsModule,
    ReactiveFormsModule,
    TranslateModule,
    PershingCoreModule,
    PershingCommonModule,
    ResponsiveModule.forRoot(),
    PershingGrid2Module,
    MatCheckboxModule,
    MatTooltipModule,
    MatCardModule,
    MatNativeDateModule,
    MatButtonModule,
    AgGridModule.withComponents([
      PershingGridHeaderComponent
    ])
  ],
  declarations: [... PERSHING_TRADING_COMPONENTS],
  providers: [... PERSHING_TRADING_SERVICE_PROVIDERS,
    TRADING_SERVICE_PROVIDERS_FACTORY,
    EQUITY_TRADING_SERVICE_PROVIDERS_FACTORY,
    MUTUAL_FUND_TRADING_SERVICE_PROVIDERS_FACTORY,
    OPTION_TRADING_SERVICE_PROVIDERS_FACTORY,
    PERSHING_TRADING_SERVICE_STRATEGIES_PROVIDERS,
    {provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher}
  ],
  entryComponents: [... PERSHING_TRADING_ENTRY_COMPONENTS],
  exports: [...PERSHING_TRADING_COMPONENTS]
})
export class PershingTradingCommonModule {
  constructor(private translationService: PershingTranslationService, @Optional() @Inject('AppSettings') appSettings: any) {
    if (this.translationService) {
        // default for wealth
        let i18nPath = 'node_modules/@pershing/trading-common/assets';
        let i18RetailPath = 'node_modules/@pershing/trading-common/assets/retail';
        let i18nModule = '@pershing/trading-common';

        if (appSettings) {
          // for running in local
          if (appSettings.tradingi18nPath) {
            i18nPath = '/assets';
            i18RetailPath = '/assets/retail';
          }
        }
        let translationArray = [i18nPath];
        if (appSettings.platform === 'retail') {
          translationArray.push(i18RetailPath);
        }
        console.log('loading translations from: ', translationArray);
        this.translationService.addTranslationFolders(i18nModule, translationArray);
    }
  }
}
